/**
 * Exclusion Rules Store - Manage proxy-level exclusion rules
 *
 * These rules are applied at history query-time, allowing users
 * to toggle visibility without losing recorded traffic.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import type { FilterRule } from '../electron/proxy-types';

export type ExclusionRule = FilterRule & {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
};

type ExclusionRulesState = {
  rules: ExclusionRule[];

  // Actions
  addRule: (
    rule: Omit<ExclusionRule, 'id' | 'createdAt' | 'updatedAt'>,
  ) => ExclusionRule;
  updateRule: (
    id: string,
    updates: Partial<Omit<ExclusionRule, 'id' | 'createdAt'>>,
  ) => void;
  deleteRule: (id: string) => void;
  toggleRule: (id: string) => void;
  duplicateRule: (id: string) => ExclusionRule | null;

  // Get enabled rules in FilterRule format for proxy
  getEnabledFilterRules: () => FilterRule[];

  // Reset
  resetToDefaults: () => void;
};

const STORAGE_KEY = 'terra-proxy-exclusion-rules-store';

// Built-in exclusion rules for common cases
const DEFAULT_RULES: Omit<ExclusionRule, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'Static Assets',
    description:
      'Exclude JavaScript, CSS, images, fonts, and other static files',
    type: 'exclude',
    pattern:
      '\\.(js|css|png|jpg|jpeg|gif|svg|ico|woff|woff2|ttf|eot|map)($|\\?)',
    isRegex: true,
    enabled: false,
  },
  {
    name: 'Analytics & Tracking',
    description: 'Exclude common analytics and tracking endpoints',
    type: 'exclude',
    pattern:
      '(google-analytics|googletagmanager|facebook|fbevents|pixel|beacon|analytics|gtag|gtm)',
    isRegex: true,
    enabled: false,
  },
  {
    name: 'WebSocket Upgrades',
    description: 'Exclude WebSocket upgrade requests',
    type: 'exclude',
    pattern: '(socket\\.io|sockjs|ws://|wss://)',
    isRegex: true,
    enabled: false,
  },
  {
    name: 'Favicon',
    description: 'Exclude favicon requests',
    type: 'exclude',
    pattern: 'favicon\\.ico',
    isRegex: true,
    enabled: false,
  },
];

const initializeDefaultRules = (): ExclusionRule[] => {
  const now = Date.now();
  return DEFAULT_RULES.map((rule, index) => ({
    ...rule,
    id: `default-${index}`,
    createdAt: now,
    updatedAt: now,
  }));
};

export const useExclusionRulesStore = create<ExclusionRulesState>()(
  persist(
    (set, get) => ({
      rules: initializeDefaultRules(),

      addRule: (ruleData) => {
        const now = Date.now();
        const newRule: ExclusionRule = {
          ...ruleData,
          id: uuidv4(),
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          rules: [...state.rules, newRule],
        }));

        return newRule;
      },

      updateRule: (id, updates) => {
        set((state) => ({
          rules: state.rules.map((rule) =>
            rule.id === id
              ? { ...rule, ...updates, updatedAt: Date.now() }
              : rule,
          ),
        }));
      },

      deleteRule: (id) => {
        set((state) => ({
          rules: state.rules.filter((rule) => rule.id !== id),
        }));
      },

      toggleRule: (id) => {
        set((state) => ({
          rules: state.rules.map((rule) =>
            rule.id === id
              ? { ...rule, enabled: !rule.enabled, updatedAt: Date.now() }
              : rule,
          ),
        }));
      },

      duplicateRule: (id) => {
        const original = get().rules.find((r) => r.id === id);
        if (!original) return null;

        const now = Date.now();
        const newRule: ExclusionRule = {
          ...original,
          id: uuidv4(),
          name: `${original.name} (Copy)`,
          enabled: false,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          rules: [...state.rules, newRule],
        }));

        return newRule;
      },

      getEnabledFilterRules: () => {
        return get()
          .rules.filter((rule) => rule.enabled)
          .map((rule) => ({
            type: rule.type,
            pattern: rule.pattern,
            isRegex: rule.isRegex,
          }));
      },

      resetToDefaults: () => {
        set({
          rules: initializeDefaultRules(),
        });
      },
    }),
    {
      name: STORAGE_KEY,
    },
  ),
);

// Hook to get enabled filter rules
export const useEnabledFilterRules = () =>
  useExclusionRulesStore((state) => state.getEnabledFilterRules());
